import { useTranslation } from '@/composables/useTranslation'
import { WebSocketProgressReport } from '@/models/Communication'
import { EanLabelType, RenderFormat } from '@/models/Ean'
import { PartialRecord, PrimitiveObject } from '@/models/GenericInterfaces'
import { Culture, Language, Progress, ProgressType, SimpleValueType } from '@/models/Global'
import Message from '@/models/Message'
import { ProductPropertyStatus, ProductsResponse } from '@/models/Product'
import { AdvancedProperty, FormattingFunction } from '@/models/Property'
import { useDefaultStore } from '@/store'
import { usePropertyStore } from '@/store/propertyStore'
import { valueTypeIsNumeric } from '@/utils/propertyUtils'
import { clamp, toNumber } from '@/utils/tools'
import { computeInstructions } from '@burde/pim-shared'
import ExcelJS from 'exceljs'
import FileSaver, { saveAs } from 'file-saver'
import { LocationQuery } from 'vue-router'
import ApiService from './ApiService'
import DataService from './DataService'
import PropertyService from './PropertyService'

const FixedDecimalFormats: PartialRecord<FormattingFunction | 'number', string> = {
  toPriceFormat: '#,##0.00',
  toWeightFormat: '#,##0.0',
  toDimensionsFormat: '#,##0',
  number: '0',
}

type ColumnFormat = keyof typeof FixedDecimalFormats
type CellId = `${number}_${number}`
type ColumnIndex = number

type ColumnMappings = {
  formatting?: Map<ColumnIndex, ColumnFormat>
}

type CellMappings = {
  status?: Map<CellId, ProductPropertyStatus>
  fallback?: Set<CellId>
  // sku?: Set<CellId>
}

class ExportService {
  private get store() {
    return useDefaultStore()
  }

  private get propertyStore() {
    return usePropertyStore()
  }

  private get appTranslate() {
    const { appTranslate } = useTranslation()
    return appTranslate
  }

  private wsExport: WebSocket | undefined
  private wsEan: WebSocket | undefined

  private hasTopLevelComma(value: string): boolean {
    let parenDepth = 0
    let inQuotes = false

    for (let i = 0; i < value.length; i++) {
      const char = value[i]

      if (char === '"') {
        inQuotes = !inQuotes
      } else if (!inQuotes && char === '(') {
        const hasClosingParen = value.slice(i + 1).includes(')')

        if (hasClosingParen) {
          parenDepth++
        }
      } else if (!inQuotes && char === ')' && parenDepth > 0) {
        parenDepth--
      } else if (!inQuotes && parenDepth === 0 && char === ',') {
        return true
      }
    }

    return false
  }

  private getCellId = (rowNumber: number, columnIndex: number): CellId => `${rowNumber}_${columnIndex}`

  async exportProductData(
    query: LocationQuery,
    output: { headerLanguage: Language; translationLanguage: Language },
    includedProperties: AdvancedProperty[],
    fileName: string,
    debugPrint?: boolean,
  ) {
    const params = DataService.getParams(
      {
        ...query,
        from: 0,
        to: -1,
        noCache: 1,
        ...(includedProperties.some((prop) => prop.defaultName.includes('image')) && { includeImages: 1 }),
        ...(this.store.productType && { productType: this.store.productType }),
      },
      {
        includeCultureExclusiveData: true,
        translationLanguage: output.translationLanguage,
      },
    )

    const ws = await ApiService.ws(`products`, params, undefined, 'v2')
    this.wsExport = ws

    const progressType: ProgressType = 'productsExport'

    const productsResponse: ProductsResponse = await new Promise((resolve, reject) => {
      this.store.clearProgress(progressType)

      const mainProgress: Progress = {
        id: 'main',
        progressType,
        currentAction: 'Exporterar data',
        percentage: 0,
      }

      this.store.addProgressBar(mainProgress)

      ws.onmessage = (e) => {
        const msg: WebSocketProgressReport<ProductsResponse> = e.data ? JSON.parse(e.data) : undefined

        if (msg.status === 'inprogress') {
          if (msg.progressPercent === 100) {
            this.store.setProgressById({ id: 'main', progressType: 'productsExport', currentAction: 'Slutför export' })
          } else if (msg.progressPercent !== undefined) {
            mainProgress.percentage = msg.progressPercent
            this.store.setProgress({
              ...mainProgress,
            } as Progress)
          }
        } else if (msg.status === 'finished' && msg.data) {
          resolve(msg.data)
          this.wsExport?.close()
        } else {
          this.wsExport?.close(1003, 'Received unexpected message')
        }
      }

      ws.onclose = (e) => {
        if (e.code !== 1000 && e.reason) {
          reject()
          this.store.addMessage(new Message(e.reason, undefined, 'danger'))
        }
        this.store.clearProgress(progressType)
      }

      ws.onerror = (e) => {
        console.error(e)
        reject()
        this.store.clearProgress(progressType)
      }
    })

    const columns = Object.values(includedProperties)

    const cellMappings: CellMappings = {
      status: new Map(),
      fallback: new Set(),
      // sku: new Set<string>(),
    }
    const columnMappings: ColumnMappings = {
      formatting: new Map<number, ColumnFormat>(),
    }

    const exportColumns = columns.map((property, columnIndex) => {
      if (property.formatting) {
        columnMappings.formatting?.set(columnIndex, property.formatting)
      }

      return {
        property,
        // isSkuProperty: isSkuProperty(property),
        header: PropertyService.getTranslatedHeader(property, output.headerLanguage),
      }
    })

    const exportedProducts = productsResponse.data.map((product, rowIndex) =>
      exportColumns.reduce((acc: PrimitiveObject, { property, header }, columnIndex) => {
        const propertyReturn = property.get(product, undefined, output.translationLanguage)

        const formattingExportInstruction = this.propertyStore.getPropertyExportFormattingInstructions(property)

        const exportValue =
          ((formattingExportInstruction && computeInstructions(formattingExportInstruction, propertyReturn?.value, property, product)) as SimpleValueType) ??
          propertyReturn?.value ??
          propertyReturn.fallback

        /** Quote values that contains unnested commas to make sure import of multi valued attributes work as intended */
        const safeExportValue =
          property.allowMultipleValues && typeof exportValue === 'string' && this.hasTopLevelComma(exportValue)
            ? `"${exportValue}"`
            : (valueTypeIsNumeric(property.valueType) && toNumber(exportValue)) || exportValue
        acc[header] = safeExportValue

        const cellId = this.getCellId(rowIndex + 2, columnIndex)

        if (propertyReturn?.status) {
          cellMappings.status?.set(cellId, propertyReturn.status)
        }

        // if (isSkuProperty && value && shouldFormatSku(value.toString())) {
        //   cellMappings.sku?.add(cellId)
        // }

        if (propertyReturn.fallback !== undefined) {
          cellMappings.fallback?.add(cellId)
        }

        return acc
      }, {}),
    )

    if (debugPrint) {
      console.log(exportedProducts, cellMappings)
    } else {
      this.toXlsx(exportedProducts, fileName, columnMappings, cellMappings)
    }
  }

  abortProductsExport() {
    // TODO should send cancel msg, not just close
    this.wsExport?.close(1000)
  }

  private async toXlsx(valueRows: PrimitiveObject[], filename: string, columnMappings?: ColumnMappings, cellMappings?: CellMappings) {
    try {
      if (valueRows.length) {
        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet()

        worksheet.columns = Object.keys(valueRows[0]).map((header) => ({ header, key: header, width: Math.max(9, header.length * 1.1) }))
        worksheet.addRows(valueRows)

        worksheet.views = [{ state: 'frozen', ySplit: 1 }]
        worksheet.getRow(1).eachCell((cell) => {
          if (!cell.font) {
            cell.font = {}
          }
          cell.font.bold = true
        })

        worksheet.columns.forEach((column, columnIndex) => {
          let longestFound = 0
          const ratio = 1.15

          const columnFormatting = columnMappings?.formatting?.get(columnIndex)
          const excelFormatting = (columnFormatting && FixedDecimalFormats[columnFormatting]) ?? '@'
          column.numFmt = excelFormatting

          column.eachCell?.({ includeEmpty: true }, (cell, rowIndex) => {
            const cellId = this.getCellId(rowIndex, columnIndex)

            const cellLength = cell.value?.toString().length || 0

            longestFound = Math.max(cellLength, longestFound)

            const cellStatus = cellMappings?.status?.get(cellId)
            const hasFallback = cellMappings?.fallback?.has(cellId)

            if (hasFallback) {
              cell.font = { color: { argb: cellStatus === 'preliminary' ? 'EE8888' : 'AAAAAA' }, italic: true }
            } else if (cellStatus === 'preliminary') {
              const red = { color: { argb: 'FF0000' } } as const
              cell.font = red

              if (!cellLength) {
                const redBorder = { ...red, style: 'thin' } as const

                cell.border = {
                  top: redBorder,
                  right: redBorder,
                  bottom: redBorder,
                  left: redBorder,
                }
              }
              // } else if (cellStatus === 'checked') {
              //   cell.fill = { fgColor: { argb: 'EEEEEE' }, type: 'pattern', pattern: 'solid' }
            }
          })
          column.width = clamp(longestFound * ratio, 9, 35)
        })

        // save file
        const buffer = await workbook.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        saveAs(blob, `${filename || 'export'}.xlsx`)
      } else {
        this.store.addMessage(new Message(this.appTranslate('Warning'), this.appTranslate('Response yielded no products to export'), 'warning'))
      }
    } catch (err) {
      this.store.addMessage(new Message(this.appTranslate('Could not export products'), err, 'danger'))
    }
  }

  async exportEanLabels(
    filterQuery: URLSearchParams,
    total: number,
    labelType: EanLabelType,
    customFileName: string | undefined,
    culture: Culture,
    renderFormat: RenderFormat,
  ) {
    try {
      const query = DataService.getParams(Object.fromEntries(filterQuery.entries()), {
        dataCountry: culture.country,
        dataLanguage: culture.language,
        translationLanguage: culture.language,
      })

      const payload = {
        query: query.toString(),
        culture,
        renderFormat,
      }

      this.wsEan = await ApiService.ws(`products/ean-labels/${labelType}`, undefined, payload, 'v2')

      const progressType: ProgressType = 'eanLabelExport'

      return new Promise((resolve, reject) => {
        if (this.wsEan) {
          this.store.clearProgress(progressType)

          const startingProgress: Progress = {
            id: 'main',
            progressType,
            currentAction: 'Förbereder...',
            percentage: 0,
            total,
          }

          this.store.addProgressBar(startingProgress)

          let filename = customFileName || 'unknown-ean-label-export'

          this.wsEan.onmessage = (e) => {
            if (typeof e.data === 'object') {
              const file = new Blob([e.data])
              FileSaver.saveAs(file, filename)
            } else {
              const msg: WebSocketProgressReport = JSON.parse(e.data)
              if (msg.status === 'finished' && msg.metadata?.filename) {
                filename = msg.metadata.filename
              } else if (msg.progress !== undefined) {
                startingProgress.percentage = (msg.progress / total) * 100
                startingProgress.processed = msg.progress
                this.store.setProgress({
                  ...startingProgress,
                  currentAction: 'Renderar etiketter',
                } as Progress)
              }
            }
          }

          this.wsEan.onclose = (e) => {
            resolve(e.reason)
            if (e.code !== 1000 && e.reason) {
              this.store.addMessage(new Message(e.reason, undefined, 'danger'))
            }
            this.store.clearProgress(progressType)
          }

          this.wsEan.onerror = (e) => {
            reject()
            console.log(e)
            this.store.clearProgress(progressType)
          }
        }
      })
    } catch (err) {
      this.store.addMessage(new Message('Kunde inte hämta etiketter', err, 'danger'))
    }
  }

  abortEanLabelExport() {
    this.wsEan?.close(1000)
  }

  async getProductRequestDocument(sku: string, dataLanguage: Language) {
    const params = new URLSearchParams({ dataLanguage })
    await ApiService.download(`products/request-document/${sku}?${params.toString()}`, `Förfrågningsunderlag ${sku}.xlsx`, 'v2')
  }

  public async getEanLabelExportPreview(filterQuery: URLSearchParams, culture: Culture) {
    const query = DataService.getParams(Object.fromEntries(filterQuery.entries()), {
      dataCountry: culture.country,
    })

    return await ApiService.get<{ included: string[]; excluded: string[] }>(`products/ean-labels/preview?${query.toString()}`, undefined, 'v2')
  }
}
export default new ExportService()
